import { ref, Ref } from 'vue';

import { ServicesApiResponse } from '@hfc/ui-core';

import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { Address } from '@/application/shared/domain/Address';
import { AddressSuggestion, AddressValidationResponse } from '@/application/shared/domain/AddressValidationResponse';

export type AddressValidationStatus = 'default' | 'valid' | 'invalid' | 'requested' | 'pending';

const cache = new Map<string, boolean>();

interface UsePhysicalAddressValidationOptions {
  validationFn: (address: Address) => Promise<ServicesApiResponse<AddressValidationResponse>>;
  allowCache?: boolean;
  onStatusChange?: (status: AddressValidationStatus) => void;
}

interface UsePhysicalAddressValidationReturn {
  suggestions: Ref<Array<AddressSuggestion>>;
  status: Ref<AddressValidationStatus>;
  validate: (address: Address) => Promise<void>;
  reset: () => void;
  setStatus: (status: AddressValidationStatus) => void;
}

export const usePhysicalAddressValidation = (
  options: UsePhysicalAddressValidationOptions
): UsePhysicalAddressValidationReturn => {
  const suggestions = ref<Array<AddressSuggestion>>([]);
  const status = ref<AddressValidationStatus>('default');
  const allowCache = options.allowCache ?? true;

  const { handleServicesApiError } = useHandleApiError();

  const getAddressKey = (address: Address): string => {
    return Object.values(address).reduce((acc, value, index) => {
      return index === 0 ? `${value}` : `${acc}|${value}`;
    }, '');
  };

  const isAddressAlreadyCached = (address: Address): boolean => {
    const key = getAddressKey(address);

    return cache.get(key) ?? false;
  };

  const validate = async (address: Address): Promise<void> => {
    if (allowCache && isAddressAlreadyCached(address)) {
      setStatus('valid');

      return;
    }

    setStatus('requested');

    const response = await options.validationFn(address);

    if (response.error) {
      setStatus('default');
      handleServicesApiError(response.error);

      return;
    }

    if (!response.data) {
      setStatus('invalid');

      return;
    }

    suggestions.value = response.data.suggestions;

    if (response.data.valid === false) {
      setStatus('pending');

      return;
    }

    if (allowCache) {
      const key = getAddressKey(address);
      cache.set(key, true);
    }

    setStatus('valid');
  };

  const reset = (): void => {
    setStatus('default');
    suggestions.value = [];
  };

  const setStatus = (newStatus: AddressValidationStatus): void => {
    status.value = newStatus;
    options.onStatusChange?.(newStatus);
  };

  return {
    suggestions,
    status,
    validate,
    reset,
    setStatus
  };
};
